import React, { useEffect, useMemo, useState } from 'react'

import { productCodeLittleLoans } from '@mediaingenuity/totallymoney.cardloanutils'
import Image from 'next/image'

import Anchor from '@totallymoney/ui/components/Anchor'
import Badge from '@totallymoney/ui/components/Badge'
import Box from '@totallymoney/ui/components/Box'
import Button from '@totallymoney/ui/components/Button'
import Card from '@totallymoney/ui/components/Card'
import Divider from '@totallymoney/ui/components/Divider'
import Flex from '@totallymoney/ui/components/Flex'
import Heading from '@totallymoney/ui/components/Heading'
import LinkButton from '@totallymoney/ui/components/LinkButton'
import Row from '@totallymoney/ui/components/Row'
import Stack from '@totallymoney/ui/components/Stack'
import Text from '@totallymoney/ui/components/Text'
import Icon from '@totallymoney/ui/icons/Icon'
import theme from '@totallymoney/ui/theme'

import useAppQueryState from '@hooks/useAppQueryState'
import useGetLoanApplicationUrl from '@hooks/useGetLoanApplicationUrl'
import useGetLoansAlternativeProductApplicationUrl from '@hooks/useGetLoansAlternativeProductApplicationUrl'
import useLogEvents from '@hooks/useLogEvents'
import useLogEvents2 from '@hooks/useLogEvents2'

import littleLoansLogo from '@images/little-loans-logo.png'

import { useSingleProviderScoreMutation } from '@queries/useSingleProviderScoreMutation'

import { getUuid } from '@utils/getUuid'

import LittleLoansModal from '../LittleLoansModal'
import LoanCategoryTooltip from '../LoanCategoryTooltip'
import LoanTypeModal from '../LoanTypeModal'

const ProductInfo = ({
    title,
    description,
}: {
    description: string
    title: string
}) => (
    <Box
        backgroundColor="grey10"
        p="space10"
        borderRadius="borderRadius10"
        minWidth={['100%', '230px']}
        maxHeight={['100%', '76px']}
    >
        <Row mb="space05" alignItems="center" space="space05">
            <Icon color={theme.grey120} icon="offersCheckMark" size={16} />
            <Text variant="100" weight="bold">
                {title}
            </Text>
        </Row>

        <Text variant="60">{description}</Text>
    </Box>
)

const DEFAULT_LOAN_AMOUNT = 2000
const DEFAULT_LOAN_TERM = 24

const LittleLoansOfferCard = () => {
    const logEvents = useLogEvents()
    const { logAlternativeProductClickEvent } = useLogEvents2()
    const { appQueryState } = useAppQueryState()
    const clickId = getUuid()

    const [isLittleLoansModalOpen, setLittleLoansModalOpen] = useState(false)
    const [isLoanTypeModalOpen, setLoanTypeModalOpen] = useState(false)

    const getLoanApplicationUrl = useGetLoanApplicationUrl()
    const getLoansAlternativeProductApplicationUrl =
        useGetLoansAlternativeProductApplicationUrl()

    const { mutate, data, isError, isLoading } =
        useSingleProviderScoreMutation()

    useEffect(() => {
        if (!appQueryState.loansScoreId) {
            return
        }
        mutate({
            loansScoreId: appQueryState.loansScoreId,
            loanAmount: DEFAULT_LOAN_AMOUNT,
            loanTerm: DEFAULT_LOAN_TERM,
            loanPurpose: 'Other',
            customerId: appQueryState.customerId,
            providerName: 'LittleLoans',
        })
    }, [appQueryState.customerId, appQueryState.scoreId])

    const littleLoansApplicationUrl = useMemo(() => {
        if (isError || !data?.scores?.length) {
            return getLoansAlternativeProductApplicationUrl({
                productId: productCodeLittleLoans,
                loanAmount: DEFAULT_LOAN_AMOUNT,
                loanTerm: DEFAULT_LOAN_TERM,
                tablePosition: 0,
            })
        }

        return getLoanApplicationUrl({
            loan: data.scores[0],
            clickId,
            loanAmount: data.loanAmount,
            loanTerm: data.loanTerm,
            tablePosition: 0,
            thirdPartyReference: '',
        })
    }, [
        isError,
        data,
        getLoansAlternativeProductApplicationUrl,
        getLoanApplicationUrl,
    ])

    const handleClickout = () => {
        logEvents.logAlternativeProductApplyEvent({
            productCode: productCodeLittleLoans,
            clickSource: 'whitelabel',
            rowNumber: 0,
            trigger: 'row',
        })

        logAlternativeProductClickEvent({
            altCreditCard: {
                name: 'Little Loans',
                productCode: productCodeLittleLoans,
                apr: 79.5,
            },
            category: 'credit_cards_alt',
            clickId,
            productRankOrder: null,
            productShownOrder: 1,
        })
    }

    return (
        <>
            <Card py="space30" px="space20" hasShadow={false} hasBorder={true}>
                <Box mb="space20">
                    <Badge text="New offer" impact="high" size="medium" />
                </Box>
                <Flex mb="space20" alignItems="center" gridColumnGap="space20">
                    <Box width={[104, 156, 172]} height={[52, 78, 86]}>
                        <Image
                            src={littleLoansLogo}
                            alt="Little Loans"
                            loading="lazy"
                            style={{
                                width: '100%',
                                height: 'auto',
                            }}
                        />
                    </Box>
                    <Flex
                        gridColumnGap="space10"
                        alignItems={['flex-start', 'center']}
                        flexDirection={['column', 'row']}
                    >
                        <Box>
                            <Heading variant="40">Little Loans</Heading>
                        </Box>

                        <LoanCategoryTooltip
                            loanCategory="Personal Loan"
                            loanId={productCodeLittleLoans}
                            onShowLoanCategoryModal={() =>
                                setLoanTypeModalOpen(true)
                            }
                        />
                    </Flex>
                </Flex>
                <Flex justifyContent="space-between" mb="space20">
                    <Flex
                        flexDirection={['column', 'column', 'row']}
                        gridRowGap="space10"
                        gridColumnGap="space05"
                        width="100%"
                        flexWrap="wrap"
                    >
                        <ProductInfo
                            title="Quick decision"
                            description="If approved, your loan could be sent quickly*"
                        />
                        <ProductInfo
                            title="Flexible loan amounts"
                            description="Borrow from £100 - £10,000"
                        />
                        <ProductInfo
                            title="High approval chances"
                            description="All credit scores welcome"
                        />
                    </Flex>
                    <Stack space="space10" display={['none', 'none', 'flex']}>
                        <Text
                            variant="80"
                            weight="bold"
                            color="light"
                            textAlign="center"
                        >
                            It’s quick - we’ll pre-fill your details
                        </Text>
                        <LinkButton
                            href={littleLoansApplicationUrl}
                            isExternalLink={true}
                            text="Get my personalised quote"
                            variant="primary"
                            onClick={handleClickout}
                            loading={isLoading}
                        />
                        <Flex justifyContent="center">
                            <Button
                                variant="tertiaryTransparent"
                                onClick={() => setLittleLoansModalOpen(true)}
                            >
                                More info
                            </Button>
                        </Flex>
                    </Stack>
                </Flex>
                <Box display={['none', 'none', 'initial']}>
                    <Divider
                        color="neutral160"
                        spacing="space20"
                        borderStyle="dashed"
                    />
                </Box>
                <Flex flexDirection={['column', 'column', 'row']} mb="space20">
                    <Row
                        space="space10"
                        backgroundColor="grey20"
                        py="space10"
                        px="space15"
                        borderRadius="borderRadius10"
                    >
                        <Icon color={theme.grey120} icon="info" size={16} />
                        <Text variant="60" weight="bold">
                            79.5% APR representative
                        </Text>
                    </Row>
                </Flex>
                <Box mb="space20">
                    <Text textAlign="center" variant="60" color="light">
                        Warning: Late repayment can cause you serious money
                        problems. For help, go to{' '}
                        <Anchor
                            style={{ display: 'inline' }}
                            target="_blank"
                            rel="noopener"
                            href="https://www.moneyhelper.org.uk"
                            aria-label="Opens in a new tab to MoneyHelper website"
                        >
                            moneyhelper.org.uk
                        </Anchor>
                        .
                    </Text>
                </Box>
                <Box display={['inline', 'inline', 'none']}>
                    <Stack space="space20" mb="space20">
                        <LinkButton
                            href={littleLoansApplicationUrl}
                            isExternalLink={true}
                            text="Get my personalised quote"
                            variant="primary"
                            onClick={handleClickout}
                            loading={isLoading}
                        />
                        <Flex justifyContent="center">
                            <Button
                                variant="tertiaryTransparent"
                                onClick={() => setLittleLoansModalOpen(true)}
                            >
                                More info
                            </Button>
                        </Flex>
                    </Stack>
                </Box>
                <Text textAlign="center" variant="60" color="light">
                    Digitonomy Limited, trading as Little Loans, is a credit
                    broker, not a lender.
                </Text>
            </Card>

            <LoanTypeModal
                showModal={isLoanTypeModalOpen}
                onModalClose={() => setLoanTypeModalOpen(false)}
            />
            <LittleLoansModal
                showModal={isLittleLoansModalOpen}
                onModalClose={() => setLittleLoansModalOpen(false)}
            />
        </>
    )
}

export default LittleLoansOfferCard
